import { Accordion, Flex, Group, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useState } from 'react';
import SectionCard from 'src/cashflows/components/SectionCard';
import { SectionAddAction, SectionAddButton } from 'src/cashflows/components/SectionAddButton';
import { formatCurrency } from 'src/cashflows/utils';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { FinancialRows } from './FinancialRowsTable';
import { FinancialRow, FinancialSubSection } from './financialRows';

const CHEVRON_SIZE = 20;

type FinancialSectionTableProps = {
  title: string;
  rows: FinancialRow[];
  total: number;
  totalLabel?: string;
  subSections?: FinancialSubSection[];
  showMember?: boolean;
  onRowClick?: (row: FinancialRow) => void;
  addAction?: SectionAddAction;
  hideTotal?: boolean;
};

export function FinancialSectionTable({
  title,
  rows,
  total,
  totalLabel = 'Total',
  subSections,
  showMember = false,
  onRowClick,
  addAction,
  hideTotal = false,
}: FinancialSectionTableProps) {
  const [openValue, setOpenValue] = useState<string | null>(null);

  const isOpen = openValue === title;

  return (
    <SectionCard
      value={title}
      title={title}
      count={rows.length}
      showCount={false}
      rightSectionPlacement="overlay"
      openValue={openValue}
      onOpenChange={setOpenValue}
      styles={{
        item: { border: 'none', borderRadius: 20, background: 'transparent' },
        control: {
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderRadius: 20,
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 22,
          paddingBottom: 22,
        },
        chevron: { display: 'none' },
        label: { paddingTop: 0, paddingBottom: 0 },
        content: { paddingTop: 32, paddingBottom: 32, paddingLeft: 24, paddingRight: 24 },
      }}
      rightSection={
        <Group
          gap={24}
          wrap="nowrap"
          align="center"
          style={{ position: 'absolute', top: 0, bottom: 0, right: 24 }}
        >
          {!hideTotal && (
            <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.4">
              {totalLabel}:{' '}
              <Text span inherit c="gray.10">
                {formatCurrency(total)}
              </Text>
            </Text>
          )}
          {addAction && <SectionAddButton action={addAction} ariaLabel={`Add ${title}`} />}
          <UnstyledButton
            onClick={() => setOpenValue(isOpen ? null : title)}
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <IconChevronDown
              size={CHEVRON_SIZE}
              color="var(--mantine-color-black)"
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </UnstyledButton>
        </Group>
      }
    >
      {subSections && subSections.length > 0 ? (
        <FinancialSubSections
          sections={subSections}
          showMember={showMember}
          onRowClick={onRowClick}
        />
      ) : (
        <FinancialRows rows={rows} showMember={showMember} onRowClick={onRowClick} />
      )}
    </SectionCard>
  );
}

function FinancialSubSections({
  sections,
  showMember,
  onRowClick,
}: {
  sections: FinancialSubSection[];
  showMember: boolean;
  onRowClick?: (row: FinancialRow) => void;
}) {
  return (
    <Accordion
      multiple
      chevron={<IconChevronDown size={18} stroke={1.5} color="var(--mantine-color-gray-9)" />}
      styles={{
        item: { border: 'none', background: 'transparent' },
        control: {
          backgroundColor: 'transparent',
          paddingLeft: 0,
          paddingRight: 0,
          paddingBottom: 0,
          paddingTop: 0,
        },
        content: { paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 0 },
      }}
    >
      {sections.map(section => (
        <Accordion.Item role="region" key={section.key} value={section.key}>
          <Accordion.Control>
            <Flex justify="space-between" align="center">
              <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
                {section.title}
              </Text>
              <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
                {formatCurrency(section.total)}
              </Text>
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            <FinancialRows rows={section.rows} showMember={showMember} onRowClick={onRowClick} />
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );
}
